import { DateAsString } from '@blk/awt-aladdinportfoliobuilderserver';
import { AxiosResponse, CancelToken } from 'axios';
import { awBridgeApi, awBridgeSettings } from '../Base';
import {
  AWPerformanceAccountDayZeroRequest,
  AWPerformanceAccountDayZeroResponse,
  AWPerformanceAggregatesResponse,
  AWPerformanceCustomAggregateAnalysisPreDefinedPeriodsRequest,
  AWPerformanceCustomAggregateAnalysisPreDefinedPeriodsResponse,
  AWPerformanceCustomDateGetResponse,
  AWPerformanceCustomDatePostResponse,
  AWPerformanceCustomDateRequest,
  AWPerformanceDecompositionBreakdownResponse,
  AWPerformanceDecompositionSecurityResponse,
  AWPerformanceOfficialAggregatesResponse,
  AWPerformanceOfficialRequest,
  AWPerformanceRequest,
} from '../Models';
import { awPerformanceDefaultRequestData, awPerformanceRootPath } from './Constants';

/**
 * Makes post call for aggregate level performance analytics for all supported periods (1d, 1w, mtd, qtd, ytd, itd).
 * @param data - data for the request body
 * @param tag - aggregate tag example 'accountId'
 * @param id - aggregate id example 'ACCT-123'
 * @return - data from response
 */
export function postAwPerformance(
  data: AWPerformanceRequest,
  tag: string,
  id: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AWPerformanceAggregatesResponse>> {
  const { getPkgFn } = awBridgeSettings;
  const pkg = getPkgFn?.();
  if (!tag || !id) {
    throw Error('Missing tag or id for performance aggregate');
  }
  const path = `${awPerformanceRootPath}/${tag}/${id}`;
  return awBridgeApi.post(path, { pkg, ...awPerformanceDefaultRequestData, ...data }, { cancelToken });
}

/**
 * Makes post call for aggregate level performance analytics for all supported periods (1d, 1w, mtd, qtd, ytd, itd) and includes official yearly returns.
 * @param data - data for the request body
 * @param currentAnalysisDate - the current analysis date, to be included in the list of requested analysis dates
 * @param tag - aggregate tag example 'accountId'
 * @param id - aggregate id example 'ACCT-123'
 * @return - data from response
 */
export function postAwPerformanceOfficial(
  data: AWPerformanceOfficialRequest,
  tag: string,
  id: string,
  currentAnalysisDate?: DateAsString,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AWPerformanceOfficialAggregatesResponse>> {
  const { getPkgFn } = awBridgeSettings;
  const pkg = getPkgFn?.();
  if (!tag || !id) {
    throw Error('Missing account tag or id for performance aggregate');
  }
  const path = `${awPerformanceRootPath}/official/${tag}/${id}`;
  const analysisDate = [...data.analysisDate];
  if (currentAnalysisDate) {
    analysisDate.push(currentAnalysisDate);
  }
  return awBridgeApi.post(
    path,
    {
      pkg,
      ...{ ...data, analysisDate },
    },
    { cancelToken }
  );
}

/**
 * Returns performance and security decomposition data of a portfolio (account/Household) for all supported periods (mtd, qtd, ytd) computed on monthly basis.
 * @param data - data for the request body
 * @param tag - aggregate/portfolio's tag example 'accountId'
 * @param id - aggregate/portfolio's id example 'ACCT-123'
 * @return - data from response
 */
export function postAwPerformanceSecurityDecomposition(
  data: AWPerformanceRequest,
  tag: string,
  id: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AWPerformanceDecompositionSecurityResponse>> {
  if (!tag || !id) {
    throw Error('Missing tag or id for performance security decomposition');
  }
  const { getPkgFn } = awBridgeSettings;
  const pkg = getPkgFn?.();
  const path = `${awPerformanceRootPath}/security-decomposition/${tag}/${id}`;
  return awBridgeApi.post(path, { pkg, ...awPerformanceDefaultRequestData, ...data }, { cancelToken });
}

/**
 * Returns data for breakdown decomposition for a portfolio (account/Household).
 * @param data - data for the request body
 * @param tag - aggregate/portfolio's tag example 'accountId'
 * @param id - aggregate/portfolio's id example 'ACCT-123'
 * @return - data from response
 */
export function postAwPerformanceBreakdownDecomposition(
  data: AWPerformanceRequest,
  tag: string,
  id: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AWPerformanceDecompositionBreakdownResponse>> {
  if (!tag || !id) {
    throw Error('Missing tag or id for performance breakdown decomposition');
  }
  const { getPkgFn } = awBridgeSettings;
  const pkg = getPkgFn?.();
  const path = `${awPerformanceRootPath}/breakdown-decomposition/${tag}/${id}`;
  return awBridgeApi.post(path, { pkg, ...awPerformanceDefaultRequestData, ...data }, { cancelToken });
}

/**
 * Returns id for data fetch for custom date perf data for a portfolio (account/Household).
 * @param data - data for the request body
 * @return - data from response
 */
export function postAwPerformanceCustomDateRequest(
  data: AWPerformanceCustomDateRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AWPerformanceCustomDatePostResponse>> {
  const path = `${awPerformanceRootPath}/custom-date-analysis`;
  return awBridgeApi.post(path, { ...awPerformanceDefaultRequestData, ...data }, { cancelToken });
}

export function getAwPerformanceCustomDateData(
  requestId: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AWPerformanceCustomDateGetResponse>> {
  const path = `${awPerformanceRootPath}/custom-date-analysis/${requestId}`;
  return awBridgeApi.get(path, { cancelToken });
}

export function getAwPerformanceAccountDayZero(
  aggIdTag: string,
  data: AWPerformanceAccountDayZeroRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AWPerformanceAccountDayZeroResponse>> {
  const path = `${awPerformanceRootPath}/${aggIdTag}/snapshot-start-date`;
  return awBridgeApi.post(path, data, { cancelToken });
}

/**
 * Submits async performance calculation for a custom aggregate view.
 * - Custom time period: Request must include periodStartDate and periodEndDate, and exclude analysisDate field
 * - Pre-defined time period: Request must include analysisDate, exclude periodStartDate and periodEndDate field
 * @param data - data for the request body
 * @param cancelToken
 * @returns requestId to use with 'getCustomAggregateAnalysisWithId' below
 */
export function postCustomAggregateAnalysis(
  data: AWPerformanceCustomAggregateAnalysisPreDefinedPeriodsRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AWPerformanceCustomAggregateAnalysisPreDefinedPeriodsResponse>> {
  const { getPkgFn } = awBridgeSettings;
  const pkg = getPkgFn?.();
  const path = `${awPerformanceRootPath}/custom-aggregate-analysis`;
  return awBridgeApi.post(path, { pkg, ...awPerformanceDefaultRequestData, ...data }, { cancelToken });
}

/**
 * Poll and fetch AW Performance data using the id-pkg key variables for a custom aggregate view.
 * @param requestId - this ID is generated by calling 'postCustomAggregateAnalysis' above
 */
export function getCustomAggregateAnalysisWithId(requestId: string): Promise<AxiosResponse> {
  const path = `${awPerformanceRootPath}/custom-aggregate-analysis/${requestId}`;
  return awBridgeApi.get(path);
}
